/**
 * @fileoverview MasterGroupedView — ClusterCode-grouped view for the **Master/Detail** tab.
 *
 * Consumes `MasterVenues` records (actually `MarketVenueMappingView` rows returned by
 * `MasterVenuesController.Filter`) from the nearest react-admin `ListContextProvider`
 * and presents them as one expandable row per unique `clusterCode` value.
 *
 * Within each expanded group, records are sub-grouped by `placeId` (see
 * {@link ClusterDetailPanel}). Each distinct sub-group shows:
 *  - One **checkbox** (for merge selection)
 *  - A clickable venue-info box that opens {@link UnifiedNameDrawer}
 *  - Market rows: Market, Venue ID, Venue Name, City
 *
 * All groups are **auto-expanded** whenever the filter results change.
 *
 * Selection state is a single `checkedRowIds` set of individual mapping-row
 * IDs, owned here and passed all the way down to each row-level checkbox. A
 * sub-group (PlaceId) header checkbox is purely derived from it — checked
 * only when every one of its rows is selected. The **Merge** button is
 * enabled only once the checked rows span **two or more distinct PlaceId
 * groups within the same ClusterCode group**; checking multiple rows within
 * a single PlaceId group does not enable it. A merge is only ever possible
 * between records sharing the same `ClusterCode` but different `PlaceId`s
 * (that constraint is also enforced server-side — see
 * `MasterVenuesController.Filter`), so once a row is checked, rows belonging
 * to a different `ClusterCode` are rejected with a warning notification
 * (`handleToggleRow`) and visually disabled (`MasterGroupRow`/
 * `ClusterDetailPanel`) until the selection is cleared. A summary beside the
 * button shows the total group and mapping counts.
 *
 * The Merge dialog ({@link MergeDialog}) uses an embedded Google Maps iframe
 * and requires a non-empty `Destination Unified Name`.
 *
 * Clicking a `MergedToId` chip opens {@link MergeHistoryDrawer} with the
 * full chain of inbound merges from the backend.
 *
 * After filter results auto-expand, merge history is loaded in bulk for all
 * visible cluster codes via `GET /MasterVenues/GetMergeHistoryByClusters`.
 * When a single group is manually expanded, the API is called for that code only.
 */

import * as React from 'react';
import { useListContext, useDataProvider, useNotify } from 'react-admin';
import {
    Box,
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import MergeIcon from '@mui/icons-material/MergeType';
import type { MasterVenueRecord, ClusterCodeGroup } from '../utils';
import { buildClusterCodeGroups } from '../utils';
import MergeDialog, { type MergeSubGroup } from './MergeDialog';
import { buildPlaceIdSubGroups } from './ClusterDetailPanel';
import MasterGroupRow, { MASTER_COL_WIDTHS } from './MasterGroupRow';
import UnifiedNameDrawer from './UnifiedNameDrawer';
import MergeHistoryDrawer from './MergeHistoryDrawer';

// Re-export so callers can import from here if they prefer.
export type { ClusterCodeGroup };
export { buildClusterCodeGroups };

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * A single market-venue mapping that was re-linked during a merge.
 * Returned by `GET /MasterVenues/GetMergeHistoryByClusters`.
 */
export interface MergedMarketRef {
    /** Numeric market identifier. */
    marketId: number;
    /** Market type string (e.g. "sm", "axs"). */
    marketType: string;
    /** Source venue identifier within the market's system. */
    sourceVenueId: number;
    /** Venue display name from the source market's data, at merge time. */
    venueName: string | null;
    /** City from the source market's venue record, at merge time. */
    city: string | null;
}

/**
 * A single entry in the cluster-scoped merge history returned by
 * `GET /MasterVenues/GetMergeHistoryByClusters`.
 */
export interface ClusterMergeHistoryEntry {
    /** Cluster Plus Code this entry belongs to. */
    clusterCode: string;
    sourceMasterVenueId: number;
    sourceGoogleUnifiedName: string | null;
    sourceFormattedAddress: string | null;
    sourceCity: string | null;
    sourceState: string | null;
    destinationMasterVenueId: number;
    destinationGoogleUnifiedName: string | null;
    destinationFormattedAddress: string | null;
    destinationCity: string | null;
    destinationState: string | null;
    /** UTC timestamp when the merge was performed. */
    updatedOn: string | null;
    /** User who performed the merge. */
    updatedBy: string | null;
    /**
     * Market-venue mappings re-linked from source to destination during this merge.
     * Used to match individual child rows by `marketId`, `marketType`, `sourceVenueId`.
     */
    mergedMarkets: MergedMarketRef[];
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** Props for {@link MasterGroupedView}. */
export interface MasterGroupedViewProps {
    /**
     * When set, the component auto-expands the cluster group that contains the
     * master venue with this ID.
     */
    expandAfterMasterVenueId?: number | null;
    /**
     * Ref to the Master/Detail filter button. After a successful merge, clicking
     * this button re-runs the current filter and reloads merge history, keeping
     * the view consistent with the server state.
     */
    refBtnFilterMaster?: React.RefObject<HTMLButtonElement>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Master/Detail tab component — groups `MasterVenues` records by `clusterCode`.
 *
 * Within each cluster, records are sub-grouped by `placeId`. Each mapping row has its
 * own checkbox; a sub-group's header checkbox is derived (checked only when all of its
 * rows are checked). When checked rows span two or more distinct PlaceId groups, the
 * **Merge** button becomes active. The merge summary shows `N Groups (M mappings)`.
 *
 * All groups auto-expand when filter results change.  After auto-expanding, merge
 * history is fetched in bulk for all visible clusters.  When a single group is
 * manually toggled open, merge history is fetched for that cluster only.
 *
 * Clicking a `UnifiedName` opens {@link UnifiedNameDrawer}; clicking a merge
 * indicator opens {@link MergeHistoryDrawer}.
 *
 * @param props.expandAfterMasterVenueId - Auto-expand the group containing this master venue.
 */
const MasterGroupedView: React.FC<MasterGroupedViewProps> = ({
    expandAfterMasterVenueId,
    refBtnFilterMaster,
}) => {
    const { data } = useListContext<MasterVenueRecord>();
    const dataProvider = useDataProvider() as any;
    const notify = useNotify();

    const groups = React.useMemo(() => buildClusterCodeGroups(data), [data]);

    /**
     * Filtered groups based on the current filter values.
     * Memoized to avoid unnecessary re-renders.
     */
    const filteredGroups = React.useMemo(() => {
        return groups.filter(x => x.records.filter(r => r.id).length > 0);
    }, [groups]);

    /** Set of currently-expanded cluster codes. */
    const [expandedCodes, setExpandedCodes] = React.useState<Set<string>>(new Set());

    // ── Merge-history state ───────────────────────────────────────────────────

    /**
     * Merge history entries keyed by cluster code.
     * Populated by {@link loadMergeHistory}.
     */
    const [mergeHistoryByCluster, setMergeHistoryByCluster] = React.useState<
        Record<string, ClusterMergeHistoryEntry[]>
    >({});

    /** Whether a merge-history API call is in flight. */
    const [mergeHistoryLoading, setMergeHistoryLoading] = React.useState(false);

    /**
     * Set of currently-checked mapping-row IDs. This is the single source of
     * truth for all selection state — sub-group (PlaceId) header checkboxes
     * and Merge eligibility are both derived from it, not tracked separately.
     */
    const [checkedRowIds, setCheckedRowIds] = React.useState<Set<string>>(new Set());

    /**
     * Fetches merge history for the given cluster codes and merges the result
     * into {@link mergeHistoryByCluster}.
     *
     * @param codes - Cluster codes to load history for.
     */
    const loadMergeHistory = React.useCallback(
        async (codes: string[]) => {
            // Filter out empty codes (no-code group) and distinct codes to avoid duplicate API calls.
            const params = Array.from(new Set(codes.filter(Boolean)));
            if (!params.length) return;

            setMergeHistoryLoading(true);
            try {
                const res = await dataProvider.postData(
                    'MasterVenues',
                    'GetMergeHistoryByClusters',
                    params,
                );
                const entries: ClusterMergeHistoryEntry[] = res.data ?? [];

                setMergeHistoryByCluster(prev => {
                    const next = { ...prev };
                    entries.forEach(e => {
                        const key = e.clusterCode ?? '';
                        if (!next[key]) next[key] = [];
                        // Avoid duplicates when the same cluster is re-loaded.
                        const existing = next[key].map(x => x.sourceMasterVenueId);
                        if (!existing.includes(e.sourceMasterVenueId)) {
                            next[key] = [...next[key], e];
                        }
                    });
                    return next;
                });
            } catch {
                // Merge history is supplementary; silently ignore errors.
            } finally {
                setMergeHistoryLoading(false);
            }
        },
        [dataProvider],
    );

    /**
     * Auto-expand all groups whenever the filtered groups change (e.g. after filtering)
     * and bulk-load merge history for all visible cluster codes.
     */
    React.useEffect(() => {
        if (!filteredGroups.length) {
            setExpandedCodes(new Set());
            setMergeHistoryByCluster({});
            return;
        }
        const codes = filteredGroups.map(g => g.clusterCode);
        setExpandedCodes(new Set(codes));
        // Reset and reload merge history for the new set of visible clusters.
        setMergeHistoryByCluster({});
        const timer = setTimeout(() => {
            loadMergeHistory(codes);
        }, 800); // 800ms is enough to consolidate redundant renders
        // Reset selection state when the filter changes, since the checked rows may no longer be present.
        setCheckedRowIds(new Set());
        // Cleanup function: Will run when filteredGroups continues to change in the next render
        return () => {
            clearTimeout(timer);
        };
    }, [filteredGroups]);

    /**
     * Lookup of mapping-row ID → owning `clusterCode`, used to enforce the
     * same-ClusterCode merge constraint in {@link handleToggleRow}.
     */
    const rowClusterCode = React.useMemo(() => {
        const map = new Map<string, string>();
        filteredGroups.forEach(g => g.records.forEach(r => map.set(r.id, g.clusterCode)));
        return map;
    }, [filteredGroups]);

    /**
     * The `ClusterCode` the current selection is scoped to, derived from the
     * first checked row. `null` when nothing is checked — any cluster may be
     * selected from next.
     */
    const activeClusterCode = React.useMemo(() => {
        if (checkedRowIds.size === 0) return null;
        const firstId = checkedRowIds.values().next().value as string;
        return rowClusterCode.get(firstId) ?? null;
    }, [checkedRowIds, rowClusterCode]);

    /** Whether the Merge dialog is open. */
    const [mergeDialogOpen, setMergeDialogOpen] = React.useState(false);

    /** Whether a merge operation is in progress. */
    const [merging, setMerging] = React.useState(false);

    // ── Map drawer state ──────────────────────────────────────────────────────
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [drawerVenue, setDrawerVenue] = React.useState<{
        unifiedName: string;
        formattedAddress: string;
        googleMapsUri: string;
    } | null>(null);

    // ── Merge-history drawer state ────────────────────────────────────────────
    const [mergeHistoryOpen, setMergeHistoryOpen] = React.useState(false);
    const [mergeHistoryVenueId, setMergeHistoryVenueId] = React.useState<number | null>(null);

    // Auto-expand after Unify / MergedToId click.
    React.useEffect(() => {
        if (!expandAfterMasterVenueId || !data?.length) return;
        const target = data.find(r => r.masterVenueId === expandAfterMasterVenueId);
        if (!target) return;
        const code = target.clusterCode || '';
        setExpandedCodes(prev => {
            if (prev.has(code)) return prev;
            const next = new Set(prev);
            next.add(code);
            return next;
        });
    }, [expandAfterMasterVenueId, data]);

    /**
     * Toggles the expanded state of a cluster group.
     *
     * @param clusterCode - The cluster code to toggle (empty string = no-code group).
     */
    const handleToggle = (clusterCode: string): void => {
        setExpandedCodes(prev => {
            const next = new Set(prev);
            if (next.has(clusterCode)) {
                next.delete(clusterCode);
            } else {
                next.add(clusterCode);
            }
            return next;
        });
    };

    /**
     * Toggles a single mapping row's checked state.
     *
     * Enforces the same-ClusterCode merge constraint: a row belonging to a
     * different `ClusterCode` than the current selection is rejected (the
     * selection is left unchanged) with a warning notification explaining
     * that the current group must be cleared first.
     *
     * @param id - The mapping row's `id`.
     */
    const handleToggleRow = (id: string): void => {
        setCheckedRowIds(prev => {
            if (prev.has(id)) {
                const next = new Set(prev);
                next.delete(id);
                return next;
            }

            if (prev.size > 0) {
                const firstId = prev.values().next().value as string;
                const existingCluster = rowClusterCode.get(firstId);
                const targetCluster = rowClusterCode.get(id);
                if (targetCluster !== existingCluster) {
                    notify(
                        'A merge selection must stay within a single Cluster Code group. Deselect the current group first.',
                        { type: 'warning' },
                    );
                    return prev;
                }
            }

            const next = new Set(prev);
            next.add(id);
            return next;
        });
    };

    /**
     * Opens the map drawer for the given venue details.
     *
     * @param unifiedName      - Venue display name.
     * @param formattedAddress - Full formatted address.
     * @param googleMapsUri    - Google Maps URI.
     */
    const handleOpenDrawer = (
        unifiedName: string,
        formattedAddress: string,
        googleMapsUri: string,
    ): void => {
        setDrawerVenue({ unifiedName, formattedAddress, googleMapsUri });
        setDrawerOpen(true);
    };

    /**
     * Opens the merge-history drawer for the given destination master venue.
     *
     * @param masterVenueId - The canonical master venue ID to show history for.
     */
    const handleOpenMergeHistory = (masterVenueId: number): void => {
        setMergeHistoryVenueId(masterVenueId);
        setMergeHistoryOpen(true);
    };

    /**
     * Collects the distinct PlaceId sub-groups, scoped to the single
     * `ClusterCode` group the current selection belongs to
     * ({@link activeClusterCode}), that have at least one checked mapping
     * row. A sub-group needs only a single checked row to count here — Merge
     * eligibility itself requires **two or more** such distinct sub-groups
     * (see `mergeEnabled`). Rows outside `activeClusterCode` can never reach
     * this array because {@link handleToggleRow} rejects them.
     */
    const selectedSubGroups = React.useMemo((): MergeSubGroup[] => {
        if (checkedRowIds.size === 0 || activeClusterCode === null) return [];
        const activeGroup = filteredGroups.find(g => g.clusterCode === activeClusterCode);
        if (!activeGroup) return [];
        const subs = buildPlaceIdSubGroups(activeGroup.records);
        return subs.filter(sg => sg.records.some(r => checkedRowIds.has(r.id)));
    }, [checkedRowIds, filteredGroups, activeClusterCode]);

    /** Total mapping record count across all filtered groups. */
    const totalMappings = React.useMemo(
        () => filteredGroups.reduce((acc, g) => acc + g.records.length, 0),
        [filteredGroups],
    );

    /**
     * Executes the merge via `POST /MasterVenues/Merge`.
     *
     * @param destinationMasterVenueId - The canonical master venue to merge into.
     * @param destinationUnifiedName   - The (optionally edited) name for the destination.
     */
    const handleMergeConfirm = async (
        destinationMasterVenueId: number,
        destinationUnifiedName: string,
    ): Promise<void> => {
        // Filter checkedRowIds down to the distinct PlaceId sub-groups that have at least one checked row.
        const sourceMasterMappingIds = selectedSubGroups
            .filter(sg => sg.masterVenueId !== destinationMasterVenueId)
            .flatMap(sg => sg.records)
            .map(x => x.id)
            .filter(id => checkedRowIds.has(String(id)));

        if (sourceMasterMappingIds.length === 0) {
            notify('No source venues to merge (destination was the only selection).', { type: 'warning' });
            setMergeDialogOpen(false);
            return;
        }

        setMerging(true);
        try {
            const res = await dataProvider.postData('MasterVenues', 'Merge', {
                sourceMasterMappingIds,
                destinationMasterVenueId,
                destinationUnifiedName,
            });
            notify(
                `Merge complete: ${res.data?.updatedMappings ?? 0} mapping(s) re-linked, ` +
                `${res.data?.updatedSources ?? 0} source venue(s) marked merged.`,
                { type: 'success', autoHideDuration: 4000 }
            );
            setCheckedRowIds(new Set());
            setMergeDialogOpen(false);
            // Re-run the current filter to reload data and merge history.
            refBtnFilterMaster?.current?.click();
        } catch (err: any) {
            notify(`Merge failed: ${err?.message ?? 'Unknown error'}`, { type: 'error', autoHideDuration: 6000 });
        } finally {
            setMerging(false);
        }
    };

    if (!filteredGroups.length) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    No master venue records to group.
                </Typography>
            </Box>
        );
    }

    const mergeEnabled = selectedSubGroups.length >= 2;

    return (
        <Box>
            {/* Merge action bar with summary */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5, mb: 1, px: 1 }}>
                <Typography variant="caption" color="text.secondary">
                    {filteredGroups.length.toLocaleString()} Groups&nbsp;
                    ({totalMappings.toLocaleString()} mappings)
                </Typography>
                {activeClusterCode !== null && (
                    <Typography variant="caption" color="warning.main">
                        Selection scoped to cluster &ldquo;{activeClusterCode || '(no cluster code)'}&rdquo;
                    </Typography>
                )}
                {mergeHistoryLoading && (
                    <CircularProgress size={14} thickness={4} sx={{ color: 'text.disabled' }} />
                )}
                <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    startIcon={<MergeIcon />}
                    disabled={!mergeEnabled || merging}
                    onClick={() => setMergeDialogOpen(true)}
                >
                    Merge ({checkedRowIds.size} selected)
                </Button>
            </Box>

            <Table size="small" aria-label="master grouped view" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: MASTER_COL_WIDTHS.chevron }} />
                        <TableCell sx={{ fontWeight: 700, width: MASTER_COL_WIDTHS.clusterCode }}>Cluster Code</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: MASTER_COL_WIDTHS.unifiedName }}>Unified Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: MASTER_COL_WIDTHS.location }}>Location</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: MASTER_COL_WIDTHS.mergedTo }}>Merged</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: MASTER_COL_WIDTHS.mergedMarkets }}>Merged Markets</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filteredGroups.map(group => (
                        <MasterGroupRow
                            key={group.clusterCode === '' ? '__empty__' : group.clusterCode}
                            group={group}
                            expanded={group.records.filter(r => r.id).length > 0 && expandedCodes.has(group.clusterCode)}
                            onToggle={() => handleToggle(group.clusterCode)}
                            checkedRowIds={checkedRowIds}
                            onToggleRow={handleToggleRow}
                            onOpenDrawer={handleOpenDrawer}
                            onOpenMergeHistory={handleOpenMergeHistory}
                            mergeHistory={mergeHistoryByCluster[group.clusterCode] ?? []}
                            mergeHistoryLoading={mergeHistoryLoading}
                            selectionDisabled={activeClusterCode !== null && activeClusterCode !== group.clusterCode}
                        />
                    ))}
                </TableBody>
            </Table>

            {/* Merge dialog */}
            <MergeDialog
                open={mergeDialogOpen}
                selectedSubGroups={selectedSubGroups}
                onConfirm={handleMergeConfirm}
                onCancel={() => setMergeDialogOpen(false)}
            />

            {/* Map preview drawer */}
            <UnifiedNameDrawer
                open={drawerOpen}
                unifiedName={drawerVenue?.unifiedName ?? ''}
                formattedAddress={drawerVenue?.formattedAddress}
                googleMapsUri={drawerVenue?.googleMapsUri}
                onClose={() => setDrawerOpen(false)}
            />

            {/* Merge-history drawer */}
            <MergeHistoryDrawer
                open={mergeHistoryOpen}
                masterVenueId={mergeHistoryVenueId}
                onClose={() => setMergeHistoryOpen(false)}
            />
        </Box>
    );
};

export default MasterGroupedView;
